import { Box } from "@mui/joy";
import type { ExcursionBooking } from "api/excursions/Excursion.typed";
import {
  formatPlanningUserShortName,
  formatPlanningBookingCardTooltipHeader,
  formatPlanningBookingCardTooltipBody,
  isPlanningBookingCrossTenantSlot,
  isPlanningBookingForeignCompany,
} from "utils/planningLeaveHelpers";
import { PlanningSlotTooltipWrap } from "./PlanningTooltip";
import {
  PlanningDayChip,
  BookingValidationFooter,
  PLANNING_BOOKING_EMOJI,
  PLANNING_BOOKING_CHIP_BG,
  PLANNING_UNAVAILABLE_EMOJI,
} from "./PlanningDayChip";

type DayBookingsGroupProps = {
  dayBookings: ExcursionBooking[];
  dayKey: string;
  currentUserId?: number;
  viewerTenantCompanyId?: number;
  getUserName?: (id: number) => string | undefined;
  canValidateAnyBooking: boolean;
  onBookingStatusChange?: (bookingId: number, status: "validated" | "rejected") => Promise<void>;
};

export default function DayBookingsGroup({
  dayBookings,
  dayKey,
  currentUserId,
  viewerTenantCompanyId,
  getUserName,
  canValidateAnyBooking,
  onBookingStatusChange,
}: DayBookingsGroupProps) {
  const grouped = new Map<number | string, ExcursionBooking[]>();
  for (const b of dayBookings) {
    const cross = isPlanningBookingCrossTenantSlot(b);
    const foreign = isPlanningBookingForeignCompany(b, viewerTenantCompanyId);
    const ownCross = (cross || foreign) && currentUserId != null && b.user_id === currentUserId;
    const key = (cross || foreign) && !ownCross ? `masked-${b.user_id}` : b.user_id;
    const arr = grouped.get(key) ?? [];
    arr.push(b);
    grouped.set(key, arr);
  }

  return (
    <>
      {Array.from(grouped.entries()).map(([key, group]) => {
        const first = group[0];
        const cross = isPlanningBookingCrossTenantSlot(first);
        const foreign = isPlanningBookingForeignCompany(first, viewerTenantCompanyId);
        const ownCross =
          (cross || foreign) && currentUserId != null && first.user_id === currentUserId;
        const isAnon = (cross || foreign) && !ownCross;
        const nameRaw = first.user_name?.trim() || getUserName?.(first.user_id)?.trim();
        const chipText = formatPlanningUserShortName(nameRaw);

        const tooltipHeader = formatPlanningBookingCardTooltipHeader(
          first,
          getUserName,
          viewerTenantCompanyId,
          currentUserId,
        );

        const tooltipBody = group
          .map((b) =>
            formatPlanningBookingCardTooltipBody(
              b,
              getUserName,
              viewerTenantCompanyId,
              currentUserId,
            ),
          )
          .filter(Boolean)
          .join("\n———\n");

        const pendingBookings = group.filter((b) => b.status === "pending");
        const isOwn = currentUserId != null && first.user_id === currentUserId;
        const managerMayAct = canValidateAnyBooking && first.user_is_employee === true;
        const canAct =
          (isOwn || managerMayAct) && pendingBookings.length > 0 && onBookingStatusChange;
        const validationFooter = canAct ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {pendingBookings.map((b) => (
              <BookingValidationFooter
                key={b.id}
                onValidate={() => onBookingStatusChange!(b.id, "validated")}
                onReject={() => onBookingStatusChange!(b.id, "rejected")}
              />
            ))}
          </Box>
        ) : undefined;

        return (
          <PlanningSlotTooltipWrap
            key={`bg-${key}-${dayKey}`}
            tooltipId={`plan-bkg-${key}-${dayKey}`}
            tooltipText={tooltipBody}
            tooltipHeader={tooltipHeader}
            tooltipFooter={validationFooter}
            leaveDelay={validationFooter ? 280 : 0}
          >
            <PlanningDayChip
              emoji={isAnon ? PLANNING_UNAVAILABLE_EMOJI : PLANNING_BOOKING_EMOJI}
              label={chipText}
              bgColor={isAnon ? undefined : PLANNING_BOOKING_CHIP_BG}
              badge={group.length > 1 ? group.length : undefined}
            />
          </PlanningSlotTooltipWrap>
        );
      })}
    </>
  );
}
